import { type CSSProperties, type FC, memo, useCallback, useMemo } from 'react';
import cn from 'classnames';
import { _notNil } from '@/littledash';
import Tooltip from '@/components/UI/Tooltip';
import { getInitials } from '@/components/UI/UserAvatar/UserAvatar';
import type { AvatarColor } from '@/constants/colors';
import type { UserApiId } from '@/model/User.model';
import styles from './UserPresenceChip.module.scss';

interface UserPresenceChipProps {
  name: string;
  color: AvatarColor;
  isActive: boolean;
  following?: boolean;
  interactive?: boolean;
  showTooltip?: boolean;
  showStatusDot?: boolean;
  testId?: string;
  userId?: UserApiId;
  onPulse?: (userId: UserApiId) => void;
  onToggleFollow?: (userId: UserApiId) => void;
}

/**
 * Circular avatar chip showing a user's initials, color, and active/inactive
 * status dot. Shared between the online-users cluster (interactive: hover to
 * pulse, click to follow) and the "N users" overlay popover list (static).
 *
 * Memoised, and the interactive handlers are derived from the stable `userId` +
 * `onPulse`/`onToggleFollow` callbacks so a parent re-render (e.g. a realtime
 * tick) doesn't re-render every chip — only the one whose props actually change.
 */
const UserPresenceChipComponent: FC<UserPresenceChipProps> = ({
  name,
  color,
  isActive,
  following = false,
  interactive = false,
  showTooltip = true,
  showStatusDot = true,
  testId,
  userId,
  onPulse,
  onToggleFollow,
}) => {
  const chipStyle = useMemo<CSSProperties>(
    () => ({
      borderColor: color.border,
      // color.fill is a 5% alpha tint that only reads correctly on white. Compose
      // the same tint over white so the interior is opaque and looks right on any
      // backdrop (e.g. the dark "N users" popover or overflow tooltip).
      backgroundColor: `color-mix(in srgb, ${color.border} 5%, white)`,
      '--chip-ring-color': color.border,
    }),
    [color.border]
  ) as CSSProperties;

  const initials = useMemo(() => getInitials(name), [name]);

  const handleMouseEnter = useCallback(() => {
    if (_notNil(userId)) {
      onPulse?.(userId);
    }
  }, [userId, onPulse]);

  const handleClick = useCallback(() => {
    if (_notNil(userId)) {
      onToggleFollow?.(userId);
    }
  }, [userId, onToggleFollow]);

  const chip = (
    <span
      className={cn(styles.chip, { [styles.interactive]: interactive, [styles.following]: following })}
      style={chipStyle}
      data-testid={testId}
      onMouseEnter={interactive ? handleMouseEnter : undefined}
      onClick={interactive ? handleClick : undefined}
    >
      <span>{initials}</span>
      {showStatusDot && (
        <span
          className={cn(styles.statusDot, isActive ? styles.statusActive : styles.statusInactive)}
          data-testid={testId ? `${testId}__status-dot` : undefined}
        />
      )}
    </span>
  );

  if (!showTooltip) {
    return chip;
  }

  return <Tooltip render={name}>{chip}</Tooltip>;
};

export const UserPresenceChip = memo(UserPresenceChipComponent);
